import { z } from 'zod';

import { normalizeArn } from '@/utils/arn';

const optionalPincode = z.union([
  z.literal(''),
  z
    .string()
    .trim()
    .regex(/^\d{4,10}$/, 'Invalid pincode'),
]);

// No object-level transform/default here: input and output types must stay
// identical or the react-hook-form resolver generics stop lining up. The ARN
// is normalized by the submit handlers instead.

/**
 * Where the lead came from (plan.md §16.2, task 9.7).
 *
 * The single client-side copy of the vocabulary, and the ONLY thing the lead
 * dialogs offer. A CLIENT MIRROR of the server's LEAD_SOURCES
 * (server/src/constants/index.ts), which the server validator consumes directly
 * via `z.nativeEnum(LEAD_SOURCES)` - so there is no third copy on the server.
 * There is no shared package between the two workspaces, so this is
 * hand-maintained: ADDING a value here without adding it on the server produces
 * a clean 422 on save, which is the cheap failure. The expensive one is renaming
 * a value on one side only, because the stored strings ARE the historical record
 * - a rename orphans every row already written under the old label. If a label
 * has to change, add the new one and migrate the rows deliberately.
 *
 * The sign-off round for Fix 1 DID retire two labels, with the product
 * owner's explicit go-ahead to re-enter the handful of affected rows:
 * 'Referral' was folded into 'Recommendation' and 'Event/Seminar' into
 * 'Webinar'. A row still holding a retired label is not lost - the edit dialog
 * renders an unrecognised stored value as its own '(as recorded)' option and
 * round-trips it untouched (see optionalLeadSource below). Treat that retirement
 * as a one-off, not as a precedent.
 */
export const LEAD_SOURCE_OPTIONS = [
  'Website',
  'Webinar',
  'Recommendation',
  'Marketing',
  'WhatsApp',
  'LinkedIn',
  'Existing Client',
  'Partner',
  'Inbound Enquiry',
  'Cold Call',
  'AMFI List',
  'Import',
  'Other',
] as const;

/**
 * NEVER required. An RM who does not know the channel must be able to save the
 * lead anyway - the alternative is a mandatory field answered at random, which
 * is worse than a blank one because it looks like data. '' means "not recorded"
 * and is stripped before the payload is built.
 *
 * NOT narrowed to LEAD_SOURCE_OPTIONS, on purpose. The list above is the
 * vocabulary the select OFFERS; it is not the set of values that can legally sit
 * in this field, because the edit dialog loads what is already stored on the
 * lead - and rows written by the importer, the seeds, or a label that predates
 * the current list hold strings this enum does not contain. Narrowing here is
 * exactly what destroyed attribution: the stored value failed the enum, fell
 * back to '', and '' was then submitted as an explicit "clear this column".
 * A form field must be able to round-trip the value it was given.
 *
 * Nothing is lost by widening: the only way to put a value in this field is to
 * pick one of the rendered options, and the server is the authority on the
 * vocabulary anyway (`z.nativeEnum(LEAD_SOURCES)` - an off-list value that
 * somehow reached a payload is a clean 422, never a silent write). 40 chars
 * matches the leads.lead_source column.
 */
const optionalLeadSource = z.string().trim().max(40, 'Lead source is too long');

export const addLeadSchema = z
  .object({
    // Client type (ADKJ-117): NON-ARN clients get a server-minted 'NA…' code, so
    // the ARN field is absent for them entirely.
    isNonArn: z.boolean(),
    arn: z.string().trim().optional(),
    firstName: z.string().trim().min(1, 'First name is required').max(60),
    middleName: z.string().trim().max(60).optional(),
    // Optional: organisations often have no last name.
    lastName: z.string().trim().max(60).optional(),
    mobile: z.string().regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number'),
    email: z.string().trim().email('Enter a valid email'),
    city: z.string().trim().max(80).optional(),
    state: z.string().trim().max(80).optional(),
    pincode: optionalPincode.optional(),
    // The firm's everyday name (Fix 1). Never required: plenty of individual ARN
    // holders trade under no brand, and a mandatory field they cannot answer gets
    // answered with their own name - which makes the column useless for the search
    // it exists to serve. 150 chars matches leads.brand_name.
    brandName: z.string().trim().max(150, 'Brand name is too long').optional(),
    leadSource: optionalLeadSource.optional(),
    // The specific behind the channel: campaign name, who referred them, which
    // event. Free text on purpose - 120 chars matches the column, and forcing
    // structure on it now would just make RMs leave it blank.
    leadSourceDetail: z.string().trim().max(120).optional(),
    // The lead-source fields go INSIDE the object; the ARN superRefine chains
    // AFTER the closing brace. Order is load-bearing - a field added after
    // .superRefine() is on a ZodEffects, not the object, and never reaches the
    // form values.
  })
  .superRefine((value, ctx) => {
    if (value.isNonArn) return;
    if (!value.arn?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['arn'], message: 'ARN is required' });
      return;
    }
    if (!normalizeArn(value.arn)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['arn'],
        message: 'ARN must be 1 to 6 digits',
      });
    }
  });

export type AddLeadFormValues = z.infer<typeof addLeadSchema>;
